import React, { useEffect } from 'react';
import {
    Button,
    Card,
    Col,
    Container,
    Icon,
    LoadingOverlay,
    Row,
    Text,
} from 'spoton-lib';
import { isFulfilled } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';

import styles from './LandingPage.module.scss';
import { useAppDispatch, useAppSelector } from 'api/store';
import {
    getBusinessHoursStatus,
    getStoreIntegrationStatus,
    restartProcess,
} from '../../MerchantOnboarding.slice';
import { PageTitle } from '../page_title';
import { ProgressIndicatorIcon } from 'features/merchant_onboarding/assets';
import {
    ActiveModal,
    BusinessHoursExistStatus,
    OnboardingStatus,
} from 'features/merchant_onboarding/MerchantOnboarding.types';
import { URL } from 'utils/constants';
import { PostActivationPage } from '../post_activation_page';
import { InformationBanner } from '../information_banner';
import { getBusinessHoursUrl, RoundedButton } from 'features/common';
import { LandingPageProps } from './LandingPage.types';
import { getMenuList } from '../../../menus/Menus.slice';

export function LandingPage({
    setActiveModal,
    setIsUserTriesToSetBusinessHours,
    onTryMenuSyncAgain,
}: LandingPageProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        isInitialPageLoading,
        businessLocationId,
        isError,
        loading,
        isActivationRequested,
        stepsList,
        onboardingStatus,
        isBusinessHoursExist,
        isExpressMerchant,
        isMenusConfirmed,
        exchangeCode,
        isSignupCompleted,
    } = useAppSelector((state) => state.merchantOnboarding);

    const { menus } = useAppSelector((state) => state.menus);

    const onClickGetStartedBtn = async () => {
        if (businessLocationId) {
            const response = await dispatch(
                getBusinessHoursStatus({ businessLocationId }),
            );

            if (response && isFulfilled(response)) {
                if (response.payload.status === 'success') {
                    setActiveModal(ActiveModal.BUSINESS_HOURS_MODAL);
                }
            }
        }
    };

    const onClickRestartBtn = async () => {
        if (businessLocationId) {
            const restartProcessRes = await dispatch(
                restartProcess({ businessLocationId }),
            );
            if (restartProcessRes && isFulfilled(restartProcessRes)) {
                if (restartProcessRes.payload.data.success) {
                    navigate(
                        {
                            search: '',
                        },
                        { replace: true },
                    );
                    await dispatch(
                        getStoreIntegrationStatus({
                            businessLocationId,
                        }),
                    );
                }
            }
        }
    };

    const onClickContactSupportLink = () => {
        window.open(URL.DOORDASH_CONTACT_SUPPORT_URL);
    };

    const onClickLocatioMenuSyncTryAgainBtn = () => {
        if (onboardingStatus === OnboardingStatus.ABANDONED) {
            onClickRestartBtn();
        } else if (onboardingStatus === OnboardingStatus.MENU_BLOCK) {
            onTryMenuSyncAgain();
        }
    };

    const getStoreData = async () => {
        if (businessLocationId) {
            const response = await dispatch(
                getStoreIntegrationStatus({
                    businessLocationId,
                }),
            );

            if (response && isFulfilled(response)) {
                if (
                    response.payload.status === 'success' &&
                    !isExpressMerchant
                ) {
                    await dispatch(
                        getMenuList({
                            businessLocationId,
                            isRpos: Boolean(!isExpressMerchant),
                        }),
                    );
                }
            }
        }
    };

    useEffect(() => {
        if (businessLocationId) {
            getStoreData();
        }
    }, [businessLocationId]);

    if (isInitialPageLoading) {
        return <LoadingOverlay data-testid="page-loading" />;
    }

    return (
        <>
            <InformationBanner />
            <Container fluid className={styles.LandingPage}>
                <Row className={styles.LandingPage_row}>
                    {stepsList.every((step) => step.isProcessCompleted) ? (
                        <PostActivationPage
                            onClickContactSupportLink={
                                onClickContactSupportLink
                            }
                        />
                    ) : (
                        <Col
                            data-testid="landing-page-steps-container"
                            className={styles.LandingPage_stepsContainer}
                        >
                            <PageTitle />
                            <div className={styles.Steps}>
                                {stepsList.map((step, index) => (
                                    <Card
                                        key={index}
                                        className={styles.Steps_card}
                                        data-testid="landing-page-steps-card"
                                    >
                                        <Container fluid>
                                            <Row align="center">
                                                <Col
                                                    width={'2rem'}
                                                    style={{
                                                        paddingLeft: '8px',
                                                    }}
                                                >
                                                    {step.isProcessCompleted ? (
                                                        <ProgressIndicatorIcon />
                                                    ) : ((index === 0 &&
                                                          !step.isProcessCompleted) ||
                                                          (index > 0 &&
                                                              stepsList[
                                                                  index - 1
                                                              ]
                                                                  .isProcessCompleted &&
                                                              !step.isProcessCompleted)) &&
                                                      isError ? (
                                                        <Icon
                                                            name="DangerSolidIconTriangle"
                                                            size={24}
                                                        />
                                                    ) : (
                                                        <Text
                                                            className={`${
                                                                styles.Steps_cardNumber
                                                            } ${
                                                                ((index === 0 &&
                                                                    isBusinessHoursExist ===
                                                                        BusinessHoursExistStatus.UPDATED) ||
                                                                    (index >
                                                                        0 &&
                                                                        stepsList[
                                                                            index -
                                                                                1
                                                                        ]
                                                                            .isProcessCompleted)) &&
                                                                !step.isProcessCompleted &&
                                                                styles.Steps_cardNumber___completed
                                                            }`}
                                                            data-testid="landing-page-steps-card-number"
                                                        >
                                                            {index + 1}
                                                        </Text>
                                                    )}
                                                </Col>
                                                <Col>
                                                    <Text
                                                        type="h6"
                                                        data-testid="landing-page-steps-card-title"
                                                    >
                                                        {step.title}
                                                    </Text>
                                                </Col>
                                                {step.key ===
                                                    'is_business_hours_confirmed' &&
                                                    onboardingStatus ===
                                                        OnboardingStatus.ABANDONED &&
                                                    isBusinessHoursExist ===
                                                        BusinessHoursExistStatus.UNKNOWN &&
                                                    isError && (
                                                        <RoundedButton
                                                            buttonText={
                                                                step.errorButtonText
                                                            }
                                                            onClick={
                                                                onClickRestartBtn
                                                            }
                                                            testId="restart-ssio-flow-btn"
                                                        />
                                                    )}
                                                {step.key ===
                                                    'is_business_hours_confirmed' &&
                                                    isBusinessHoursExist ===
                                                        BusinessHoursExistStatus.NOT_UPDATED &&
                                                    onboardingStatus !==
                                                        OnboardingStatus.ABANDONED &&
                                                    isError && (
                                                        <RoundedButton
                                                            buttonText="Set up hours"
                                                            onClick={() => {
                                                                setIsUserTriesToSetBusinessHours(
                                                                    true,
                                                                );
                                                                window.open(
                                                                    getBusinessHoursUrl(
                                                                        Boolean(
                                                                            isExpressMerchant,
                                                                        ),
                                                                    ),
                                                                    '_blank',
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                {step.key ===
                                                    'is_business_hours_confirmed' &&
                                                    isBusinessHoursExist ===
                                                        BusinessHoursExistStatus.UPDATED &&
                                                    !step.isProcessCompleted && (
                                                        <RoundedButton
                                                            buttonText={
                                                                step.buttonText
                                                            }
                                                            onClick={() => {
                                                                setActiveModal(
                                                                    ActiveModal.BUSINESS_HOURS_MODAL,
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                {step.key === 'menuStatus' &&
                                                    stepsList[index - 1]
                                                        .isProcessCompleted &&
                                                    !step.isProcessCompleted &&
                                                    onboardingStatus ===
                                                        OnboardingStatus.PENDING &&
                                                    !isSignupCompleted && (
                                                        <RoundedButton
                                                            buttonText={
                                                                step.buttonText
                                                            }
                                                            onClick={() => {
                                                                if (
                                                                    !isExpressMerchant &&
                                                                    menus.length >
                                                                        1 &&
                                                                    !isMenusConfirmed
                                                                ) {
                                                                    setActiveModal(
                                                                        ActiveModal.MENU_SELECTION_MODAL,
                                                                    );
                                                                } else if (
                                                                    !exchangeCode
                                                                ) {
                                                                    setActiveModal(
                                                                        ActiveModal.DOORDASH_CONNECT_MODAL,
                                                                    );
                                                                } else {
                                                                    setActiveModal(
                                                                        ActiveModal.PRICE_MARKUP_MODAL,
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                {step.key === 'menuStatus' &&
                                                    stepsList[index - 1]
                                                        .isProcessCompleted &&
                                                    !step.isProcessCompleted &&
                                                    isError && (
                                                        <RoundedButton
                                                            buttonText={
                                                                step.errorButtonText
                                                            }
                                                            testId="location-menu-sync-try-again-btn"
                                                            onClick={
                                                                onClickLocatioMenuSyncTryAgainBtn
                                                            }
                                                        />
                                                    )}
                                                {step.key ===
                                                    'activateStatus' &&
                                                    stepsList[index - 1]
                                                        .isProcessCompleted &&
                                                    !step.isProcessCompleted &&
                                                    !isError && (
                                                        <RoundedButton
                                                            testId="activate-btn"
                                                            isLoading={
                                                                isActivationRequested
                                                            }
                                                            onClick={() => {
                                                                setActiveModal(
                                                                    ActiveModal.ACTIVATE_INTEGRATION_MODAL,
                                                                );
                                                            }}
                                                            buttonText={
                                                                step.buttonText
                                                            }
                                                        />
                                                    )}
                                                {step.key ===
                                                    'activateStatus' &&
                                                    stepsList[index - 1]
                                                        .isProcessCompleted &&
                                                    !step.isProcessCompleted &&
                                                    isError && (
                                                        <RoundedButton
                                                            onClick={() => {
                                                                setActiveModal(
                                                                    ActiveModal.ACTIVATE_INTEGRATION_MODAL,
                                                                );
                                                            }}
                                                            buttonText={
                                                                step.errorButtonText
                                                            }
                                                        />
                                                    )}
                                            </Row>
                                        </Container>
                                    </Card>
                                ))}
                            </div>
                            {!stepsList.some(
                                (step) => step.isProcessCompleted,
                            ) &&
                                isBusinessHoursExist ===
                                    BusinessHoursExistStatus.UNKNOWN &&
                                !isError && (
                                    <Button
                                        className={
                                            styles.LandingPage_getStartedButton
                                        }
                                        data-testid="get-started-btn"
                                        onClick={onClickGetStartedBtn}
                                        isLoading={loading}
                                    >
                                        Get started
                                    </Button>
                                )}
                        </Col>
                    )}
                </Row>
            </Container>
        </>
    );
}
